import { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import FormalRVHeader from '../components/formalrv/FormalRVHeader'
import Explorer from '../components/formalrv/Explorer'
import Prose from '../components/formalrv/Prose'
import Workspace from '../components/formalrv/Workspace'
import { LESSONS, type Lesson, type LessonPane } from '../components/formalrv/lessons'
import { EXERCISES, readProgress } from './FormalRVExercisePage'

/** A section of the index: a highlighted title and one aligned row per entry. */
function Section({ id, title, aside, children }: { id?: string; title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-20">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h2 className="text-xl font-bold text-qcloud-primary">{title}</h2>
        {aside}
      </div>
      <div className="bg-white rounded-xl border border-qcloud-border divide-y divide-slate-100">{children}</div>
    </section>
  )
}

function LessonIndex() {
  const location = useLocation()
  useEffect(() => {
    if (location.hash === '#exercises') document.getElementById('exercises')?.scrollIntoView()
  }, [location.hash])
  const solved = readProgress().solved
  const done = EXERCISES.exercises.filter(e => solved[e.id]).length

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-10">
      <header>
        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent">
          Learn the QParam stack
        </h1>
        <p className="text-qcloud-muted mt-2">
          Six lessons, then exercises: write programs FormalRV compiles in your browser, and read real specs, proofs and audits.
        </p>
      </header>

      <Section title="Lessons">
        {LESSONS.map((l, i) => (
          <Link key={l.id} to={`/shor/learn/${l.id}`}
            className="group grid grid-cols-[2.5rem_minmax(0,15rem)_minmax(0,1fr)] items-center gap-4 px-4 py-3 hover:bg-slate-50">
            <span className="w-7 h-7 rounded-full bg-qcloud-primary/10 text-qcloud-primary text-sm font-bold flex items-center justify-center">{i + 1}</span>
            <span className="font-semibold text-qcloud-text group-hover:text-qcloud-primary truncate">{l.title}</span>
            <span className="text-sm text-qcloud-muted truncate"><Prose text={l.summary} /></span>
          </Link>
        ))}
      </Section>

      <Section id="exercises" title="Exercises" aside={<span className="text-sm text-qcloud-muted">{done} / {EXERCISES.exercises.length} solved</span>}>
        {EXERCISES.units.map((u, i) => (
          <div key={u.id} className="grid md:grid-cols-[2.5rem_minmax(0,15rem)_minmax(0,1fr)] items-center gap-x-4 gap-y-2 px-4 py-3">
            <span className="hidden md:flex w-7 h-7 rounded-full bg-slate-100 text-qcloud-text text-sm font-bold items-center justify-center">{i + 1}</span>
            <div className="min-w-0">
              <div className="font-semibold text-qcloud-text">{u.title}</div>
              <div className="text-xs text-qcloud-muted">{u.chapter}</div>
            </div>
            <div className="flex gap-1 overflow-x-auto">
              {EXERCISES.exercises.filter(e => e.unit === u.id).map(e => (
                <Link key={e.id} to={`/shor/learn/exercises/${e.id}`} title={e.kind === 'review' ? 'read real files' : 'write a program'}
                  className={`shrink-0 inline-flex items-center gap-1.5 h-7 px-2 rounded-md border text-[13px] whitespace-nowrap transition-colors ${
                    solved[e.id] ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-qcloud-border bg-white text-qcloud-text hover:border-qcloud-primary hover:text-qcloud-primary'}`}>
                  <span aria-hidden className={`w-1.5 h-1.5 rounded-full ${e.kind === 'review' ? 'bg-amber-500' : 'bg-qcloud-primary'}`} />
                  {e.title}
                  {solved[e.id] && <span aria-label="solved" className="text-emerald-600">✓</span>}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </Section>
      <p className="-mt-7 text-xs text-qcloud-muted flex gap-4">
        <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-qcloud-primary" /> write a program</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> read real files</span>
      </p>
    </main>
  )
}

function Check({ q, a }: { q: string; a: string }) {
  return (
    <details className="group rounded-lg border border-qcloud-border">
      <summary className="cursor-pointer px-3 py-2 text-sm text-qcloud-text list-none flex justify-between gap-2">
        <Prose text={q} /><span className="text-qcloud-primary text-xs group-open:hidden">answer</span>
      </summary>
      <p className="px-3 pb-2 text-sm text-qcloud-muted"><Prose text={a} /></p>
    </details>
  )
}

function LessonView({ lesson, index }: { lesson: Lesson; index: number }) {
  const [variant, setVariant] = useState<string | null>(lesson.variant ?? null)
  const [pane, setPane] = useState<LessonPane>(lesson.pane)
  const [tryNote, setTryNote] = useState<string | null>(null)
  const prev = LESSONS[index - 1]
  const next = LESSONS[index + 1]

  useEffect(() => {
    setVariant(lesson.variant ?? null); setPane(lesson.pane); setTryNote(null)
    document.title = `${lesson.title} · Learn QParam`
  }, [lesson])

  const side = (
    <div className="space-y-5">
      <div>
        <Link to="/shor/learn" className="text-xs text-qcloud-muted hover:text-qcloud-primary">← Lessons</Link>
        <div className="text-xs font-semibold text-qcloud-muted mt-3">Lesson {index + 1} of {LESSONS.length}</div>
        <h1 className="text-2xl font-bold text-qcloud-primary leading-tight">{lesson.title}</h1>
      </div>
      <div className="space-y-3 text-[14.5px] leading-relaxed text-qcloud-text">
        {lesson.body.map((p, i) => <p key={i}><Prose text={p} /></p>)}
      </div>
      {lesson.tryIt.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-wider text-qcloud-muted">Try it</h2>
          <div className="flex flex-wrap gap-1.5">
            {lesson.tryIt.map(t => (
              <button key={t.label} onClick={() => { setVariant(t.variant); if (t.pane) setPane(t.pane); setTryNote(t.note) }}
                className={`h-8 px-3 rounded-md border text-sm ${tryNote === t.note ? 'border-qcloud-primary bg-qcloud-primary/5 text-qcloud-primary' : 'border-qcloud-border hover:border-qcloud-primary'}`}>
                {t.label}
              </button>
            ))}
          </div>
          {tryNote && <p className="text-sm text-qcloud-text bg-slate-50 rounded-lg px-3 py-2"><Prose text={tryNote} /></p>}
        </div>
      )}
      <div className="space-y-2">
        <h2 className="text-xs font-bold uppercase tracking-wider text-qcloud-muted">Check yourself</h2>
        {lesson.check.map(c => <Check key={c.q} q={c.q} a={c.a} />)}
      </div>
      <nav className="flex justify-between gap-3 pt-3 border-t border-slate-100 text-sm">
        {prev ? <Link to={`/shor/learn/${prev.id}`} className="text-qcloud-primary">← {prev.title}</Link> : <span />}
        {next
          ? <Link to={`/shor/learn/${next.id}`} className="font-semibold text-qcloud-primary text-right">{next.title} →</Link>
          : <Link to="/shor/learn#exercises" className="font-semibold text-qcloud-primary text-right">Exercises →</Link>}
      </nav>
    </div>
  )

  return (
    <Workspace crumb="Learn" side={side} sideLabel="Lesson">
      <Explorer key={`${lesson.id}-${pane}`} exampleId={lesson.example} variantId={variant} onVariantChange={setVariant} initialPane={pane} />
    </Workspace>
  )
}

export default function FormalRVLearnPage() {
  const { lessonId } = useParams<{ lessonId?: string }>()
  const index = LESSONS.findIndex(l => l.id === lessonId)

  useEffect(() => { if (!lessonId) document.title = 'Learn QParam · FT Shor Leaderboard' }, [lessonId])

  if (lessonId && index >= 0) return <LessonView lesson={LESSONS[index]} index={index} />
  return (
    <div className="min-h-screen bg-gradient-to-br from-qcloud-light via-white to-qcloud-bg">
      <FormalRVHeader crumb={lessonId ? 'Learn' : undefined} />
      {lessonId
        ? <main className="max-w-3xl mx-auto px-6 py-12 text-qcloud-muted">No such lesson. <Link to="/shor/learn" className="text-qcloud-primary">All lessons</Link></main>
        : <LessonIndex />}
    </div>
  )
}
